import React, { useState } from "react";
import { motion } from "framer-motion";
import { REMEDY_GROUPS } from "@/lib/content";
import { Reveal, EASE } from "@/lib/motion";

export default function RemediesGallery() {
  const [active, setActive] = useState(REMEDY_GROUPS[0].id);
  const activeGroup = REMEDY_GROUPS.find((g) => g.id === active);

  return (
    <section id="remedies" className="py-20 sm:py-28 bg-[#FDFBF7] relative overflow-hidden">
      <div className="absolute top-0 -left-24 w-[26rem] h-[26rem] rounded-full bg-[#E7D5A8]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8">
        <Reveal className="mb-10 sm:mb-12">
          <div className="eyebrow mb-3">The full system • 38 remedies</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-[#0B302F] tracking-tight leading-tight max-w-3xl">
            One flower for every emotion.
          </h2>
          <p className="mt-4 text-[#0B302F]/70 max-w-2xl leading-relaxed">
            Dr. Bach grouped the 38 essences into seven emotional families. Tap a group to
            explore each remedy — every one drawn from a wild flower, plant or tree.
          </p>
        </Reveal>

        <div className="flex flex-wrap gap-2 mb-10">
          {REMEDY_GROUPS.map((g) => {
            const on = g.id === active;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setActive(g.id)}
                data-testid={`remedy-group-${g.id}`}
                className={
                  "text-xs sm:text-[11px] tracking-[0.22em] uppercase px-4 h-9 rounded-full border transition-colors " +
                  (on
                    ? "bg-[#0B302F] text-[#FDFBF7] border-[#0B302F]"
                    : "bg-white text-[#0B302F]/75 border-[#EAE4DA] hover:border-[#D5B876] hover:text-[#0B302F]")
                }
              >
                {g.title}
              </button>
            );
          })}
        </div>

        <motion.div
          key={activeGroup.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5"
        >
          {activeGroup.remedies.map((r, i) => (
            <motion.figure
              key={r.id}
              data-testid={`remedy-${r.id}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, ease: EASE, delay: i * 0.04 }}
              className="group"
            >
              <div className="relative aspect-square overflow-hidden rounded-2xl border border-[#EAE4DA] bg-[#F6F2EB]">
                <img
                  src={`/flowers/${r.id}.png`}
                  alt={`${r.name} flower`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
              </div>
              <figcaption className="mt-3 px-1">
                <div className="font-serif text-lg text-[#0B302F] leading-tight">{r.name}</div>
                <div className="mt-1 text-xs text-[#0B302F]/65 leading-snug">{r.note}</div>
              </figcaption>
            </motion.figure>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
